import {
  Component,
  EventEmitter,
  Output,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';

@Component({
  selector: 'lib-image-upload',
  standalone: true,
  imports: [],
  template: `
    <div class="upload-container" [class.dragover]="isDragOver">
      <input
        [id]="inputId"
        type="file"
        accept="image/*"
        [attr.aria-label]="label || 'Upload image'"
        (change)="handleImageChange($event)"
      />
      <label class="upload-label" [for]="inputId">{{
        label || 'Upload image'
      }}</label>
      <div
        class="drop-target"
        (drop)="handleDrop($event)"
        (dragover)="handleDragOver($event)"
        (dragleave)="handleDragLeave($event)"
      >
        <p>Drag and drop an image here, or click to select one</p>
      </div>
      @if (image) {
      <img [src]="image" alt="Image preview" class="image-preview" />
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      .upload-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        border: 2px dashed var(--border, #ccc);
        border-radius: var(--border-radius-md, 8px);
        padding: 1rem;
        position: relative;
        background: var(--surface, var(--background));
        transition: all var(--animation-duration-fast) var(--animation-easing);
      }
      .upload-container.dragover {
        border-color: var(--primary);
        background: color-mix(in srgb, var(--primary) 10%, transparent);
      }
      .drop-target {
        width: 100%;
        height: 150px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        cursor: pointer;
        color: var(--muted);
        font-family: var(--font-body);
      }
      .upload-label {
        align-self: flex-start;
        color: var(--foreground);
        font-weight: 600;
      }
      .drop-target:hover {
        color: var(--foreground);
      }
      .image-preview {
        max-width: 300px;
        margin-top: 1rem;
        border-radius: var(--border-radius-md, 4px);
        box-shadow: var(--shadow-md);
      }
      input[type='file'] {
        position: absolute;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
      }
    `,
  ],
})
export class ImageUploadComponent implements OnInit {
  private static nextInputId = 0;
  @Input() label = 'Upload image';
  readonly inputId = `otui-image-upload-${++ImageUploadComponent.nextInputId}`;
  @Output() imageUpload = new EventEmitter<string>();
  @Input() currentImage: string | null = null;
  image: string | null = null;
  isDragOver = false;

  handleImageChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.readFile(file);
    }
  }

  ngOnInit(): void {
    if (this.currentImage) {
      this.image = this.currentImage;
    }
  }

  @HostListener('drop', ['$event'])
  handleDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files[0];
    if (file) {
      this.readFile(file);
    }
  }

  @HostListener('dragover', ['$event'])
  handleDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  @HostListener('dragleave', ['$event'])
  handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  private readFile(file: File): void {
    const reader = new FileReader();
    reader.onloadend = () => {
      this.image = reader.result as string;
      this.imageUpload.emit(this.image);
    };
    reader.readAsDataURL(file);
  }
}
